import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Clock3,
  Save,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import api from "@/lib/api";

export default function BusinessProfile() {
  const nav = useNavigate();

  const [form, setForm] = useState({
    business_name: "",
    business_type: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    opening_time: "",
    closing_time: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    
    const loadProfile = async () => {
      try {
        const { data } = await api.get("/business");

        if (cancelled) return;

        const b = data?.business;

        if (!data?.configured || !b) {
          return;
        }

        setForm({
          business_name: b.business_name || "",
          business_type: b.industry || "",
          phone: b.phone || "",
          email: b.email || "",
          address: b.address?.line || "",
          city: b.address?.city || "",
          state: b.address?.state || "",
          pincode: b.address?.pincode || "",
          opening_time: b.opening_time || "",
          closing_time: b.closing_time || "",
        });
      } catch (error) {
        console.error("Failed to load business profile:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, []);


  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));

    setSaved(false);
  };

 const handleSave = async (e) => {
  e.preventDefault();

  setSaving(true);
  setSaved(false);
  setSaveMessage("");

  try {
    const payload = {
      business_name: form.business_name,
      industry: form.business_type,
      phone: form.phone,
      email: form.email,
      address: {
        line: form.address,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
      },
      opening_time: form.opening_time,
      closing_time: form.closing_time,
    };

    const response = await api.put("/settings/business-profile", payload);

    // Read message returned by backend
    setSaveMessage(
      response?.data?.message || "Business profile updated successfully"
    );

    setSaved(true);

    setTimeout(() => {
      setSaved(false);
      setSaveMessage("");
    }, 3000);
  } catch (error) {
    console.error(
      "Failed to save business profile:",
      error?.response?.data || error
    );

    setSaveMessage(
      error?.response?.data?.detail ||
        "Failed to update business profile"
    );

    setSaved(false);
  } finally {
    setSaving(false);
  }
};

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F7F2]">
        <Sidebar />

        <main className="min-h-screen lg:pl-[250px]">
          <div className="mx-auto max-w-[1120px] px-4 py-8 sm:px-6 lg:px-8">
            <div className="animate-pulse">
              <div className="h-3 w-32 rounded bg-slate-200" />
              <div className="mt-3 h-8 w-64 rounded bg-slate-200" />

              <div className="mt-8 h-[400px] rounded-[20px] bg-white" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F2] text-slate-900">
      <Sidebar />

      <main className="min-h-screen lg:pl-[250px]">
        <div className="mx-auto w-full max-w-[1120px] px-4 py-5 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="mb-5"
          >
            <button
              type="button"
              onClick={() => nav("/business-settings")}
              className="
                mb-4
                inline-flex
                items-center
                gap-1.5
                text-[10px]
                font-semibold
                text-[#102C2D]
                transition
                hover:text-[#075B59]
              "
            >
              <ArrowLeft size={13} strokeWidth={1.8} />
              Business Settings
            </button>

            <div className="flex items-center justify-between gap-4">
              <div>

                <h1 className="mt-1 text-[24px] font-semibold tracking-[-0.035em] text-slate-900">
                  Business Profile
                </h1>

                <p className="mt-1 text-[10px] text-slate-400">
                  Manage your business information and operating details.
                </p>
              </div>

              {saved && (
                <motion.div
                  initial={{ opacity: 0, y: -12, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                  className="pointer-events-none fixed left-1/2 top-5 z-[9999] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-center gap-3 rounded-xl border border-[#A7E4C0] bg-[#E8F8EE] px-4 py-3 shadow-[0_12px_35px_rgba(22,101,52,0.12)]"
                >
                 <CheckCircle2 size={18} className="shrink-0 text-[#15803D]" />
                  <div className="min-w-0">
                    <p className="text-[12px] font-semibold text-[#166534]">
                      Changes saved successfully
                    </p>
                    <p className="mt-0.5 text-[10px] text-[#38744F]">
                      Your business profile is up to date.
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Profile form */}
          <motion.form
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            onSubmit={handleSave}
          >
            {/* Business information */}
            <section className="rounded-[18px] border border-[#E0E8E3] bg-white shadow-[0_4px_18px_rgba(16,44,45,0.04)]">
              <div className="flex items-center gap-3 border-b border-[#E9EEE8] px-5 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-[#E8F4EF] text-[#075B59] border border-[#D9EDE5]">
                  <Building2 size={19} strokeWidth={1.7} />
                </div>

                <div>
                  <h2 className="text-[14px] font-semibold text-slate-900">
                    Business Information
                  </h2>

                  <p className="mt-0.5 text-[9px] text-slate-400">
                    Basic details about your business.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 p-5 md:grid-cols-2">
                <Field
                  label="Business Name"
                  name="business_name"
                  value={form.business_name}
                  onChange={handleChange}
                  placeholder="Enter business name"
                  icon={Building2}
                  required
                  disabled
                />

                <Field
                  label="Business Type"
                  name="business_type"
                  value={form.business_type}
                  onChange={handleChange}
                  placeholder="e.g. Salon, Clinic, Studio"
                  icon={Building2}
                  disabled
                />

                <Field
                  label="Phone Number"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  icon={Phone}
                />

                <Field
                  label="Email Address"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter business email"
                  icon={Mail}
                  type="email"
                />
              </div>
            </section>

            {/* Address */}
            <section className="mt-4 rounded-[18px] border border-[#DFE8E2] bg-white shadow-[0_5px_20px_rgba(15,23,42,0.045)]">
              <div className="flex items-center gap-3 border-b border-[#E9EEE8] px-5 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-[#E8F4EF] text-[#075B59] border border-[#D9EDE5]">
                  <MapPin size={19} strokeWidth={1.7} />
                </div>

                <div>
                  <h2 className="text-[14px] font-semibold text-slate-900">
                    Business Address
                  </h2>

                  <p className="mt-0.5 text-[9px] text-slate-400">
                    Where your business operates.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 p-5 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Field
                    label="Address"
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Street address, building, area"
                    icon={MapPin}
                  />
                </div>

                <Field
                  label="City"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  placeholder="City"
                />

                <Field
                  label="State"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  placeholder="State"
                />

                <Field
                  label="PIN Code"
                  name="pincode"
                  value={form.pincode}
                  onChange={handleChange}
                  placeholder="PIN code"
                />
              </div>
            </section>

            {/* Operating hours */}
            <section className="mt-4 rounded-[18px] border border-[#DFE8E2] bg-white shadow-[0_5px_20px_rgba(15,23,42,0.045)]">
              <div className="flex items-center gap-3 border-b border-[#E9EEE8] px-5 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-[#E8F4EF] text-[#075B59] border border-[#D9EDE5]">
                  <Clock3 size={19} strokeWidth={1.7} />
                </div>

                <div>
                  <h2 className="text-[14px] font-semibold text-slate-900">
                    Operating Hours
                  </h2>

                  <p className="mt-0.5 text-[9px] text-slate-400">
                    Set your regular business hours.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 p-5 md:grid-cols-2">
                <Field
                  label="Opening Time"
                  name="opening_time"
                  value={form.opening_time}
                  onChange={handleChange}
                  type="time"
                />

                <Field
                  label="Closing Time"
                  name="closing_time"
                  value={form.closing_time}
                  onChange={handleChange}
                  type="time"
                />
              </div>
            </section>

            {/* Footer actions */}
            <div className="mt-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => nav("/business-settings")}
                className="
                  rounded-[9px]
                  border
                  border-[#DFE8E2]
                  bg-white
                  px-4
                  py-2.5
                  text-[10px]
                  font-semibold
                  text-slate-600
                  transition
                  hover:bg-[#F7F7F2]
                "
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
                className="
                  inline-flex
                  items-center
                  gap-1.5
                  rounded-[9px]
                  border
                  border-[#075B59]
                  bg-[#075B59]
                  px-4
                  py-2.5
                  text-[10px]
                  font-semibold
                  text-white
                  shadow-[0_4px_14px_rgba(7,91,89,0.20)]
                  transition
                  hover:border-[#064B49]
                  hover:bg-[#064B49]
                  disabled:cursor-not-allowed
                  disabled:opacity-60
                "
              >
                <Save size={13} strokeWidth={1.8} />

                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </motion.form>
        </div>
      </main>
    </div>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  placeholder,
  icon: Icon,
  type = "text",
  required = false,
  disabled = false,
}) {
  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block text-[9px] font-semibold uppercase tracking-[0.08em] text-slate-500"
      >
        {label}
      </label>

      <div className="relative">
        {Icon && (
          <Icon
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            strokeWidth={1.7}
          />
        )}

        <input
          id={name}
          name={name}
          type={type}
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={`
            h-10
            w-full
            rounded-[10px]
            border
            border-[#DFE8E2]
            px-3
            text-[12px]
            outline-none
            transition
            placeholder:text-slate-300
            focus:ring-2
            focus:border-[#0F7C82]
            focus:ring-[#E8F4EF]

            ${
              disabled
                ? "cursor-not-allowed bg-slate-100 font-meduim text-slate-700 opacity-70"
                : "bg-white text-slate-900"
            }

            ${Icon ? "pl-9" : ""}
          `}
        />
      </div>
    </div>
  );
}
